import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { motion } from 'motion/react';
import {
  DollarSign,
  Users,
  TrendingUp,
  Link as LinkIcon,
  Copy,
  Check,
  Calendar,
  ExternalLink,
  Loader2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useAffiliateData, useAcceptAffiliateInvitation } from '@/hooks/useAffiliates';
import { useAuth } from '@/contexts/AuthContext';

export const Route = createFileRoute('/affiliate/dashboard')({
  component: AffiliateDashboard,
});

function AffiliateDashboard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { data: affiliateData, isLoading, error } = useAffiliateData();
  const acceptInvitation = useAcceptAffiliateInvitation();
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [selectedDestination, setSelectedDestination] = useState('new-york-ny');
  const [selectedMotivation, setSelectedMotivation] = useState('romantic_getaway');
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [signupData, setSignupData] = useState({
    displayName: currentUser?.displayName || '',
    paymentEmail: currentUser?.email || '',
  });

  // Handle not logged in
  if (!currentUser) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Card>
          <CardContent className="pt-6 text-center">
            <h2 className="text-2xl font-bold mb-4">Sign In Required</h2>
            <p className="text-muted-foreground mb-6">
              Please sign in to access your affiliate dashboard
            </p>
            <Button onClick={() => navigate({ to: '/' })}>Go to Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Handle loading
  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Handle error or no affiliate account - show signup
  if (error || !affiliateData) {
    if (showSignupForm) {
      return (
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card>
            <CardHeader>
              <CardTitle>Join Affiliate Program</CardTitle>
              <CardDescription>
                Complete your affiliate profile to get started
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="displayName">Display Name</Label>
                <Input
                  id="displayName"
                  value={signupData.displayName}
                  onChange={(e) =>
                    setSignupData({ ...signupData, displayName: e.target.value })
                  }
                  placeholder="Your name or brand"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="paymentEmail">Payment Email</Label>
                <Input
                  id="paymentEmail"
                  type="email"
                  value={signupData.paymentEmail}
                  onChange={(e) =>
                    setSignupData({ ...signupData, paymentEmail: e.target.value })
                  }
                  placeholder="Where you want to receive payments"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => acceptInvitation.mutate(signupData)}
                  disabled={acceptInvitation.isPending || !signupData.displayName}
                  className="flex-1"
                >
                  {acceptInvitation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Joining...
                    </>
                  ) : (
                    'Join Affiliate Program'
                  )}
                </Button>
                <Button variant="outline" onClick={() => setShowSignupForm(false)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Card>
          <CardContent className="pt-6 text-center">
            <h2 className="text-2xl font-bold mb-4">Not Yet an Affiliate</h2>
            <p className="text-muted-foreground mb-6">
              You don't have an affiliate account yet. Click below to join our affiliate program and start earning!
            </p>
            <Button onClick={() => setShowSignupForm(true)}>
              Join Affiliate Program
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // TODO: Implement referrals fetching
  const recentReferrals: any[] = [];

  const conversionRate =
    affiliateData.totalClicks > 0
      ? ((affiliateData.totalConversions / affiliateData.totalClicks) * 100).toFixed(1)
      : '0.0';

  const averageOrderValue =
    affiliateData.totalConversions > 0
      ? (affiliateData.totalEarnings / affiliateData.commissionRate / affiliateData.totalConversions).toFixed(2)
      : '0.00';

  const generateAffiliateLink = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/${selectedDestination}/${selectedMotivation}?utm_source=${affiliateData.affiliateId}&utm_medium=affiliate&utm_campaign=dashboard`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedLink(text);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopiedLink(null), 2000);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">
            Affiliate Dashboard
          </h1>
          <p className="text-muted-foreground mt-2">
            Welcome back, {affiliateData.displayName}! Track your performance
            and earnings.
          </p>
          <div className="mt-4">
            <Badge variant="outline" className="text-base px-4 py-2">
              Your Affiliate Code:{' '}
              <code className="ml-2 font-mono font-bold">
                {affiliateData.affiliateId}
              </code>
            </Badge>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Clicks
              </CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{affiliateData.totalClicks}</div>
              <p className="text-xs text-muted-foreground">
                {affiliateData.totalSignups} signups
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Conversions
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {affiliateData.totalConversions}
              </div>
              <p className="text-xs text-muted-foreground">
                {conversionRate}% conversion rate
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Pending Earnings
              </CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">
                ${affiliateData.pendingEarnings.toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground">
                {affiliateData.commissionRate * 100}% commission rate
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Earned
              </CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                ${affiliateData.totalEarnings.toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground">
                ${affiliateData.paidEarnings.toFixed(2)} paid out
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Link Generator */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Generate Affiliate Links</CardTitle>
              <CardDescription>
                Create custom tracking links for different destinations and trip
                types
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="destination">Destination</Label>
                    <Select
                      value={selectedDestination}
                      onValueChange={setSelectedDestination}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new-york-ny">New York</SelectItem>
                        <SelectItem value="paris-france">Paris</SelectItem>
                        <SelectItem value="tokyo-japan">Tokyo</SelectItem>
                        <SelectItem value="london-uk">London</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="motivation">Trip Type</Label>
                    <Select
                      value={selectedMotivation}
                      onValueChange={setSelectedMotivation}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="romantic_getaway">
                          Romantic Getaway
                        </SelectItem>
                        <SelectItem value="family_vacation">
                          Family Vacation
                        </SelectItem>
                        <SelectItem value="adventure_trip">
                          Adventure Trip
                        </SelectItem>
                        <SelectItem value="business_travel">
                          Business Travel
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="affiliate-link">Your Affiliate Link</Label>
                  <div className="flex gap-2">
                    <Input
                      id="affiliate-link"
                      value={generateAffiliateLink()}
                      readOnly
                      className="font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(generateAffiliateLink())}
                    >
                      {copiedLink === generateAffiliateLink() ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => window.open(generateAffiliateLink(), '_blank')}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Share this link with your audience to track referrals
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Performance Metrics */}
          <Card>
            <CardHeader>
              <CardTitle>Performance Metrics</CardTitle>
              <CardDescription>Your key statistics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium">Conversion Rate</span>
                    <span className="text-sm font-bold">{conversionRate}%</span>
                  </div>
                  <div className="w-full bg-secondary rounded-full h-2">
                    <div
                      className="bg-primary rounded-full h-2"
                      style={{ width: `${Math.min(parseFloat(conversionRate) * 2, 100)}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      Avg. Order Value
                    </span>
                    <span className="text-sm font-semibold">
                      ${averageOrderValue}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      Click-to-Signup
                    </span>
                    <span className="text-sm font-semibold">
                      {affiliateData.totalClicks > 0
                        ? ((affiliateData.totalSignups / affiliateData.totalClicks) * 100).toFixed(1)
                        : '0.0'}%
                    </span>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      Signup-to-Conversion
                    </span>
                    <span className="text-sm font-semibold">
                      {affiliateData.totalSignups > 0
                        ? ((affiliateData.totalConversions / affiliateData.totalSignups) * 100).toFixed(1)
                        : '0.0'}%
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Referrals */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Referrals</CardTitle>
            <CardDescription>Your latest referral activity</CardDescription>
          </CardHeader>
          <CardContent>
            {recentReferrals.length > 0 ? (
              <div className="space-y-4">
                {recentReferrals.map(referral => (
                  <div
                    key={referral.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <Calendar className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{referral.date}</p>
                        <p className="text-sm text-muted-foreground">
                          {referral.status === 'converted' ? 'Conversion' : 'Sign Up'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {referral.status === 'converted' ? (
                        <>
                          <div className="text-right">
                            <p className="font-semibold text-green-600">
                              +${referral.commission?.toFixed(2)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              ${referral.amount?.toFixed(2)} sale
                            </p>
                          </div>
                          <Badge className="bg-green-100 text-green-800">
                            Converted
                          </Badge>
                        </>
                      ) : (
                        <Badge variant="outline">Signed Up</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No referrals yet. Start sharing your affiliate links!</p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
